import { CartItemProps, getCalculatedProduct, productAndAddonsToCartItems } from 'lib/seasonUtils';
import { ProgramProductInCart } from 'types/program';
import { rcEnums } from 'types/rc.enums';
import { GetRecoilValue, ReadWriteSelectorOptions, RecoilState, selector } from 'recoil';

import { Product } from 'types/product';
import { CheckoutArea, ProgramCheckoutStore } from 'types/checkout';

import {
	CheckoutFactory,
	checkoutKeyGen,
	CheckoutCartBookingFeeFactory,
	CheckoutCartTotalPriceFactory,
	CheckoutEventApplicationsFactory,
} from 'stores/checkoutStoreLib';
import { SelectedAddonInterface } from 'types/addon';
import { ResourceInterface } from 'types/resources';
import { UserSettings } from 'types/userSettings';
import { ResourceNameTypeEnum } from '@bondsports/types';
import { calculateProductQuantity } from 'lib/productsUtils';

const AREA = CheckoutArea.program;
const KEYS = checkoutKeyGen(AREA);

const checkout = CheckoutFactory<ProgramCheckoutStore>({ key: KEYS.Checkout });

const checkoutCartItems = CheckoutCartItemsFactory({ key: KEYS.CheckoutCartItems }, checkout);

const checkoutCartBookingFee = CheckoutCartBookingFeeFactory({
	key: KEYS.CheckoutCartBookingFee,
});

const checkoutCartTotalPrice = CheckoutCartTotalPriceFactory({ key: KEYS.CheckoutCartTotalPrice }, checkoutCartItems);

const checkoutEventApplications = CheckoutEventApplicationsFactory<ProgramCheckoutStore>(
	{ key: KEYS.CheckoutEventApplications },
	checkout
);

export const checkoutStore = {
	checkout,
	checkoutCartItems,
	checkoutCartBookingFee,
	checkoutCartTotalPrice,
	checkoutEventApplications,
};

export function CheckoutCartItemsFactory<T = never>(
	options: Partial<ReadWriteSelectorOptions<ProgramProductInCart[]>>,
	checkout: RecoilState<T>
) {
	const defaultOptions = {
		key: '',
		get: (opts: { get: GetRecoilValue }) => {
			let allProducts: ProgramProductInCart[] = [];
			const checkoutState = opts.get(checkout) as ProgramCheckoutStore;

			if (!checkoutState.selectedProgram || !checkoutState.selectedSeason || !checkoutState.selectedProduct) {
				return [];
			}

			const resourceType = checkoutState.productResources?.resourceType ?? ResourceNameTypeEnum.EVENT;
			const dataForCartItem = {
				previousCartItems: checkoutState.stepsCompleteProduct ?? [],
				program: checkoutState.selectedProgram,
				product: checkoutState.selectedProduct,
				season: checkoutState.selectedSeason,
				resources: {resourceType, ...checkoutState.selectedResources, isAll: !!checkoutState.isAll},
				addons: (checkoutState.selectedAddons ?? []).map((addon: SelectedAddonInterface) => {
					let newAddon: Product = addon.package as Product;
					newAddon = {
						...newAddon,
						resources: addon.resources as ResourceInterface,
					};
					return newAddon;
				}),
				sport: checkoutState.currentSport ?? rcEnums.shared.SportsEnum.BASEBALL,
				answers: checkoutState.selectedAnswers ?? [],
				quantity: calculateProductQuantity(checkoutState.selectedProduct.quantity, checkoutState.selectedResources?.quantity),
			} as CartItemProps;

			if (!checkoutState.selectedFamilyMember || (checkoutState.selectedFamilyMember as UserSettings[]).length < 1) {
				allProducts = productAndAddonsToCartItems(dataForCartItem);
			} else if ((checkoutState.selectedFamilyMember as UserSettings[]).length > 0) {
				(checkoutState.selectedFamilyMember as UserSettings[]).forEach(selectedFamilyMember => {
					const products = productAndAddonsToCartItems({
						...dataForCartItem,
						addons: dataForCartItem.addons.filter(
							addon => !checkoutState.deletedAddons?.[addon.id]?.includes(selectedFamilyMember.id)
						),
						product: getCalculatedProduct(
							checkoutState.selectedProduct as Product,
							checkoutState.calcPriceResponse || [],
							selectedFamilyMember.id
						),
						member: selectedFamilyMember,
					});
					allProducts.push(...products);
				});
			}

			const otherProducts = (checkoutState.stepsCompletedOtherProducts ?? []).reduce((aggregateArray, item) => {
				const dataForCartItem = {
					resources: item.resources,
					sport: item.sport ?? rcEnums.shared.SportsEnum.BASEBALL,
					addons:
						item.addons?.map((addon: Product) => {
							const newAddon: Product = {
								...addon,
								resources: addon.resources as ResourceInterface,
							};
							return newAddon;
						}) ?? [],
					answers: item.answers ?? [],
				} as CartItemProps;

				const products = productAndAddonsToCartItems({
					...dataForCartItem,
					addons: [],
					product: getCalculatedProduct(
						item.product,
						checkoutState.calcPriceResponse || [],
						(item.member as UserSettings).id
					),
					member: item.member,
				});

				aggregateArray.push(...products);

				return aggregateArray;
			}, [] as ProgramProductInCart[]);

			return allProducts.concat(otherProducts);
		},
	};

	const config = Object.assign(defaultOptions, options);

	return selector<ProgramProductInCart[]>(config);
}
